<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Background</label>
          <v-row>
            <v-col cols="12" md="6">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Desktop</div>
                <UploadImage
                  @callback="handleUploadBgDesktop"
                  :image="{ imageSrc: fields.background.desktop }"
                  :height="`120`"
                  @callbackDelete="handleRemoveBgDesktop"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Mobile</div>
                <UploadImage
                  @callback="handleUploadBgMobile"
                  :image="{ imageSrc: fields.background.mobile }"
                  :height="`120`"
                  @callbackDelete="handleRemoveBgMobile"
                />
              </div>
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item mb-4">
            <div class="font-weight-bold mb-2">Title</div>
            <ckeditor :editor="editor" v-model="fields.header.title" :config="editorConfig" />
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Left Icon</div>
                <v-checkbox v-model="fields.header.iconLeft.show" label="Show icon" hide-details dense class="mb-2" />
                <UploadImage
                  @callback="handleUploadIconLeft"
                  :image="{ imageSrc: fields.header.iconLeft.src }"
                  :height="`100`"
                  @callbackDelete="handleRemoveIconLeft"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Right Icon</div>
                <v-checkbox v-model="fields.header.iconRight.show" label="Show icon" hide-details dense class="mb-2" />
                <UploadImage
                  @callback="handleUploadIconRight"
                  :image="{ imageSrc: fields.header.iconRight.src }"
                  :height="`100`"
                  @callbackDelete="handleRemoveIconRight"
                />
              </div>
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <label class="label-input">Description</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.description" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Signature</label>
          <div class="group-item">
            <v-checkbox v-model="fields.signature.show" label="Show signature" hide-details dense class="mb-2" />
            <UploadImage
              @callback="handleUploadSignature"
              :image="{ imageSrc: fields.signature.src }"
              :height="`80`"
              @callbackDelete="handleRemoveSignature"
            />
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Stat Items</label>
            <v-btn small text color="primary" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Item</span>
            </v-btn>
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.items" :key="`sowhatbuy-reward-item-${index}`">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center" style="width: 100%">
                  <span>{{ index + 1 }}. {{ item.value || item.label || 'Item' }}</span>
                  <v-btn icon @click.stop="handleRemoveItem(index)">
                    <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field v-model="item.value" label="Value" hide-details class="mb-4" />
                <div class="mb-4">
                  <label class="d-block mb-1">Icon</label>
                  <UploadImage
                    @callback="obj => handleUploadItemIcon(obj, index)"
                    :image="{ imageSrc: item.icon || '' }"
                    :height="`80`"
                    @callbackDelete="() => handleRemoveItemIcon(index)"
                  />
                </div>
                <v-text-field v-model="item.label" label="Label" hide-details />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const DEFAULT_STAT_ICON = 'https://cdn.skyfastimg.com/images/2026/09/09/image-6aa0befafd3c4e85e443bc08.png?w=500';

export default {
  name: 'sowhatbuy-reward-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Sowhatbuy reward',
        enableAnchor: false,
        settings: {},
        containerPage: {},
        background: {
          desktop: '',
          mobile: '',
        },
        header: {
          title: '',
          iconLeft: {
            show: true,
            src: '',
          },
          iconRight: {
            show: true,
            src: '',
          },
        },
        description: '',
        signature: {
          show: true,
          src: '',
        },
        items: [],
      },
    };
  },
  watch: {
    sectionEditor: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val?.show && val.sectionExtract) {
          this.applyExtract(val.sectionExtract);
        }
      },
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        background: { ...this.fields.background, ...(extract.background || {}) },
        header: {
          ...this.fields.header,
          ...(extract.header || {}),
          iconLeft: { ...this.fields.header.iconLeft, ...((extract.header || {}).iconLeft || {}) },
          iconRight: { ...this.fields.header.iconRight, ...((extract.header || {}).iconRight || {}) },
        },
        signature: { ...this.fields.signature, ...(extract.signature || {}) },
        items: Array.isArray(extract.items) ? extract.items.map(item => ({ ...item })) : [],
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleUploadBgDesktop(obj) {
      if (obj?.imageSrc) this.fields.background.desktop = obj.imageSrc;
    },
    handleRemoveBgDesktop() {
      this.fields.background.desktop = '';
    },
    handleUploadBgMobile(obj) {
      if (obj?.imageSrc) this.fields.background.mobile = obj.imageSrc;
    },
    handleRemoveBgMobile() {
      this.fields.background.mobile = '';
    },
    handleUploadIconLeft(obj) {
      if (obj?.imageSrc) this.fields.header.iconLeft.src = obj.imageSrc;
    },
    handleRemoveIconLeft() {
      this.fields.header.iconLeft.src = '';
    },
    handleUploadIconRight(obj) {
      if (obj?.imageSrc) this.fields.header.iconRight.src = obj.imageSrc;
    },
    handleRemoveIconRight() {
      this.fields.header.iconRight.src = '';
    },
    handleUploadSignature(obj) {
      if (obj?.imageSrc) this.fields.signature.src = obj.imageSrc;
    },
    handleRemoveSignature() {
      this.fields.signature.src = '';
    },
    handleAddItem() {
      this.fields.items.push({
        value: '0',
        icon: DEFAULT_STAT_ICON,
        label: 'Label',
      });
    },
    handleRemoveItem(index) {
      this.fields.items.splice(index, 1);
    },
    handleUploadItemIcon(obj, index) {
      if (obj?.imageSrc) {
        this.$set(this.fields.items[index], 'icon', obj.imageSrc);
      }
    },
    handleRemoveItemIcon(index) {
      this.$set(this.fields.items[index], 'icon', '');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionSowhatbuyReward(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        html: updateHtml,
        title: this.fields.title,
      });
      this.handleClosePopup();
    },
  },
};
</script>
